<script>
import {hotels} from './providers/schemas';
import {debounce} from 'lodash';
import HotelResultsDropdown from './HotelResultsDropdown.vue';
import {compareTwoStrings} from 'string-similarity';

export default {
  components: {
    HotelResultsDropdown,
  },
  data() {
    return {
      query: '',
      results: [],
      active: false,
      autocompleteService: null,
      placeDetailsService: null,
      /* Good to know for when to show results dropdown */
      searchHasBeenUsed: false,
      skipInputBlur: false,
      /* Search term used for special searches. Example: "premier inn" */
      searchTerm: window.searchTerm || null,
    };
  },
  computed: {
    showResults() {
      return this.searchHasBeenUsed || this.results.length > 0;
    },
    placeholder() {
      if (this.searchTerm) {
        return this.$t('placeholders.findYourHotelWithTerm', {term: this.capitalizeWords(this.searchTerm)})
      }
      return this.$t('placeholders.findYourHotel');
    },
    showNearMeText() {
      if (this.searchTerm) {
        return this.$t('messages.showHotelsNearMeWithTerm', {term: this.capitalizeWords(this.searchTerm)})
      }
      return this.$t('messages.showHotelsNearMe');
    }
  },
  mounted() {
    this.autocompleteService = new google.maps.places.AutocompleteService();
    this.placeDetailsService = new google.maps.places.PlacesService(document.createElement('div'));
  },
  methods: {
    capitalizeWords(text) {
      if (!text) {
        return "";
      }
      const words = text.toLowerCase().split(" ");
      return words.map(word => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }).join(" ")
    },
    onInputFocus() {
      this.active = true;
      this.fetchHotels(this.query);
    },
    onInputBlur() {
      setTimeout(() => {
        if (this.skipInputBlur) {
          this.skipInputBlur = false;
          this.$refs.queryInput.focus();
          return;
        }
        this.active = false;
        this.searchHasBeenUsed = false;
        this.results = [];
      }, 100);
    },
    async onQueryInput(value) {
      this.query = value;
      this.fetchHotels(this.query);
    },
    fetchHotels: debounce(async function (query, lat, lng) {
      if ((!query || query.length < 3) && !lat && !lng) {
        this.results = [];
        this.searchHasBeenUsed = false;
        return;
      }

      if (this.searchTerm) {
        query = `${this.searchTerm} ${query}`;
      }

      let hotelResults = [
        ...(await this.$client
          .query({
            query: hotels,
            fetchPolicy: 'cache-first',
            variables: {
              serviceCountry: window.serviceCountryCode,
              filter: {
                query: query,
                lat,
                lng,
              },
            },
          })
          .then(({data: {hotels}}) => {
            return hotels.map(hotel => {
              if (!hotel.image_urls || !hotel.image_urls.length || !hotel.image_urls[0]) {
                return {
                  ...hotel,
                  image_urls: [require('images/placeholders/hotel.png')],
                };
              }
              return hotel;
            }).filter(hotel => {
              /* Additional filter */
              if (!this.searchTerm) {
                return true
              }
              const hotelLower = hotel.name.toLowerCase();
              return hotelLower.includes(this.searchTerm)
            });
          })),
      ];

      if (!hotelResults || hotelResults.length < 5) {
        let foundLodgings = [];

        let locationOptions = null;
        let radius = null;

        if (lat && lng) {
          radius = 5000;
          locationOptions = new google.maps.LatLng(lat, lng);
        }

        await this.autocompleteService.getPlacePredictions(
          {
            input: query || 'hotel',
            componentRestrictions: {
              country: window.serviceCountryCode,
            },
            location: locationOptions,
            radius,
            types: ['establishment'],
          },
          (predictions, status) => {
            if (status === 'OK' && predictions.length > 0) {
              for (const prediction of predictions) {
                const placeName = prediction?.description?.toLowerCase();
                if (prediction.types.includes('lodging')) {
                  if (this.searchTerm && placeName.includes(this.searchTerm)) {
                    foundLodgings.push(prediction);
                  } else {
                    foundLodgings.push(prediction);
                  }
                }
              }
            }
          }
        );

        if (foundLodgings && foundLodgings.length > 0) {
          // call details for each found lodging
          let detailsPromises = [];
          for (const lodging of foundLodgings) {
            if (hotelResults.length + detailsPromises.length >= 5) {
              break;
            }
            detailsPromises.push(
              new Promise((resolve, reject) => {
                this.placeDetailsService.getDetails(
                  {
                    placeId: lodging.place_id,
                    fields: ['name', 'formatted_address', 'photos', 'geometry', 'place_id'],
                  },
                  (result, status) => {
                    resolve(result);
                  }
                );
              })
            );
          }

          await Promise.allSettled(detailsPromises).then(async detailsResults => {
            for (const detailsResult of detailsResults) {
              if (detailsResult.status === 'fulfilled' && detailsResult.value) {
                const place = detailsResult.value;
                let photo = null;

                if (place.photos && place.photos.length) {
                  photo = await place.photos[0]?.getUrl({
                    maxWidth: 256,
                  });
                }

                if (!photo) {
                  photo = require('images/placeholders/hotel.png');
                }

                const formattedHotel = {
                  address: place.formatted_address,
                  name: place.name,
                  image_urls: [photo],
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng(),
                  id: place.place_id,
                  google: true,
                };

                if (!hotelResults || !hotelResults.length) {
                  hotelResults.push(formattedHotel);
                  continue;
                }

                const distanceThreshold = 0.05;
                const similarityThreshold = 0.55;

                /* Filter out similar hotels based on criteria */
                let includeHotel = true;
                for (const hotel of hotelResults) {
                  if (hotel.google) {
                    continue;
                  }

                  /* Check if hotel is close */
                  const distance = this.getDistance(
                    {lat: hotel.lat, lng: hotel.lng},
                    {lat: formattedHotel.lat, lng: formattedHotel.lng}
                  );
                  const hotelIsCloseBy = distance < distanceThreshold;

                  /* Check if hotel names are similar */
                  const similarity = compareTwoStrings(hotel.name, formattedHotel.name);

                  const hotelNamesAreSimilar = similarity > similarityThreshold;

                  /* If hotels are close by and names are similar, don't include it in results */
                  if (hotelIsCloseBy && hotelNamesAreSimilar) {
                    includeHotel = false;

                    /* Check if existing hotel from our backend has no image */
                    if (!hotel.image_urls?.length && formattedHotel.image_urls.length) {
                      hotel.image_urls.push(formattedHotel.image_urls[0]);
                    }
                    break;
                  }
                }

                if (includeHotel) {
                  hotelResults.push(formattedHotel);
                }
              }
            }
          });
        }
      }

      this.active = true;
      this.searchHasBeenUsed = true;

      this.results = hotelResults;
    }, 300),
    getDistance(start, end, decimals) {
      /* Returns distance in kilometers */
      decimals = decimals || 2;
      var earthRadius = 6371; // km

      var lat1 = parseFloat(start.lat);
      var lat2 = parseFloat(end.lat);
      var lon1 = parseFloat(start.lng);
      var lon2 = parseFloat(end.lng);

      var dLat = (lat2 - lat1).toRad();
      var dLon = (lon2 - lon1).toRad();
      lat1 = lat1.toRad();
      lat2 = lat2.toRad();

      var a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
      var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      var d = earthRadius * c;
      return Math.round(d * Math.pow(10, decimals)) / Math.pow(10, decimals);
    },
    bookDirectly() {
      window.open(window.bookingUrl);
    },
    showHotelsNearMe() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            this.$refs.queryInput.focus();
            this.query = '';
            this.fetchHotels(this.query, position.coords.latitude, position.coords.longitude);
          },
          error => {
          }
        );
      }
    },
    onResultsFocused() {
      this.skipInputBlur = true;
    },
  },
};
</script>

<template>
  <div>
    <div
      :class="{ 'has-results': showResults, active: showResults || active }"
      class="hotel-search-wrapper"
    >
      <div class="hotel-search-box mb-16">
        <img :src="require('images/icons/search_circle.svg')" alt="Search" height="32" width="32"/>
        <input
          ref="queryInput"
          :placeholder="placeholder"
          :value="query"
          class="hotel-search-input"
          type="text"
          @blur="e => onInputBlur(e)"
          @focus="onInputFocus"
          @input="e => onQueryInput(e.target.value)"
        />
      </div>
      <div
        v-if="showResults"
        ref="hotelResultsWrapper"
        class="hotel-search-results--wrapper"
        tabindex="1"
        @focus="onResultsFocused"
      >
        <HotelResultsDropdown
          :results="results"
          :showNearMeText="showNearMeText"
          @bookDirectly="bookDirectly"
          @nearMe="showHotelsNearMe"
        />
      </div>
    </div>
    <a class="d-block ps-16 show-near-me" href @click.prevent="showHotelsNearMe">
      {{ showNearMeText }}
    </a>
  </div>
</template>
